import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { IssueScopeKind } from '../../../../../shared/issuesV2';
import { window } from '../../../../globals';
import { UNSET_POLICY_OPTION } from '../../../shared/policies/hooks/usePolicies';
import SBOMSection from '../../../shared/settings/SettingsPageSBOMSection';
import Section from '../../../shared/settings/SettingsPageSection';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { SBOMSettings } from '../../../../../shared/settings/project/sbom';

import PropagateSBOMModal from './PropagateSBOMModal';
import type { IssueSettingsProps } from './types';

/**
 * SettingsOrganizationSBOM is the settings page for
 * managing the SBOM policy settings for projects.
 */
const SettingsOrganizationSBOM: VoidFunctionComponent<IssueSettingsProps> = ({ policies }) => {
  const [isPropagateModalOpen, setIsPropagateModalOpen] = useState<boolean>(false);
  const { settings, isLoading, onUpdateSettings, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<SBOMSettings>({
      endpoint: `/api/organizations/${window.user?.organizationId as number}/settings/projects/sbom`,
      settingsSection: 'project SBOM',
    });
  const selectedPolicy =
    policies.find((policy) => policy.value === settings?.defaultSBOMPolicyId) || UNSET_POLICY_OPTION;
  return (
    <>
      <Section>
        <Section.Left heading="SBOMs">
          <Section.Paragraph>Default configuration for imported SBOM projects.</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton onClick={() => setIsPropagateModalOpen(true)} isDisabled={!isSaveButtonDisabled} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <SBOMSection
            scopeType={IssueScopeKind.Global}
            sbomAnalysisEnabled={settings?.projectSBOMAnalysisEnabled || false}
            onToggleSBOMAnalysis={(isChecked) => onUpdateSettings({ projectSBOMAnalysisEnabled: isChecked })}
            policyOptions={policies}
            selectedPolicyOption={selectedPolicy}
            onSelectedPolicyChange={(policyId) => onUpdateSettings({ defaultSBOMPolicyId: policyId })}
            canEditPolicy
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagateSBOMModal
          settings={settings}
          isOpen={isPropagateModalOpen}
          onHide={() => setIsPropagateModalOpen(false)}
          policyName={selectedPolicy.label}
        />
      )}
    </>
  );
};

export default SettingsOrganizationSBOM;
